/**
 * Columns the RDV templates emit themselves.
 *
 * The live list comes from GET /rdv-generator-owned-columns, which serves the generator's
 * own GENERATOR_OWNED_COLUMNS constant. The set below is only the offline fallback for a
 * failed or not-yet-returned request; the backend remains the source of truth. Hub and satellite generation strips these from the modelled column
 * list before building the SQL, so modelling one has no effect on the output, and
 * flagging one as a business key drags a technical column into the hub hash (breaking
 * the sat -> hub join) or empties the hash entirely.
 *
 * Links are deliberately excluded from this rule: a link may legitimately key on a
 * technical column — SGDS relates sales orders to their source system via md_system_id —
 * and the link template does not strip them.
 */
export const FALLBACK_GENERATOR_OWNED_COLUMNS = new Set([
  "is_latest",
  "md_deleted_ts",
  "md_effective_ts",
  "md_lddts",
  "md_pipeline_run_id",
  "md_record_source",
  "md_system_id"
]);

/** True when the column name is one the hub/satellite templates emit themselves. */
export function isGeneratorOwnedColumn(
  name?: string | null,
  reserved: Set<string> = FALLBACK_GENERATOR_OWNED_COLUMNS
): boolean {
  if (!name) {
    return false;
  }
  return reserved.has(name.trim().toLowerCase());
}

/**
 * Whether a modelled row collides with a template-owned column for this construct.
 * `entityType` matches the modelling canvas node kinds ("hub" | "satellite" | "link").
 */
export function isTemplateOwnedRow(
  entityType: string | null | undefined,
  targetColumnName?: string | null,
  sourceColumnName?: string | null,
  reserved: Set<string> = FALLBACK_GENERATOR_OWNED_COLUMNS
): boolean {
  if (entityType !== "hub" && entityType !== "satellite") {
    return false;
  }
  return (
    isGeneratorOwnedColumn(targetColumnName, reserved) ||
    (!targetColumnName && isGeneratorOwnedColumn(sourceColumnName, reserved))
  );
}
